import React from "react";
import MessageBubble from "./MessageBubble";
import TypingIndicator from "./TypingIndicator";
import ChatConversationSkeleton from "@/features/chat-agui/ChatConversationSkeleton";
import FloatingChatInput from "@/components/chat/FloatingChatInput";
import FloatingChatComposer from "@/components/chat/FloatingChatComposer";
import ChatEmptyState from "@/components/chat/ChatEmptyState";
import { formatTimestamp } from "../../../utils/chatHelpers";
import { CarFront, DollarSign, File, Ship } from "lucide-react";

export const suggestions = [
  { icon: <CarFront size={16} />, label: "Starting a new shipment" },
  { icon: <DollarSign size={16} />, label: "Checking shipment prices" },
  { icon: <File size={16} />, label: "Reviewing vehicle documents" },
  { icon: <Ship size={16} />, label: "Recommending a good port" },
];

const composer = ({
  input,
  setInput,
  handleSend,
  handleUploadClick,
  loading,
  inputRef,
  handleKeyPress,
  selectedFile,
  onRemoveFile,
  fileInputRef,
  documentInputRef,
  handleFileSelect,
  voiceInputProps = {},
}) => (
  <FloatingChatComposer>
    <input ref={fileInputRef} type="file" hidden accept="image/*" onChange={handleFileSelect} />
    <input
      ref={documentInputRef}
      type="file"
      hidden
      accept=".pdf,.doc,.docx,.txt"
      onChange={handleFileSelect}
    />
    <FloatingChatInput
      multiline
      input={input}
      setInput={setInput}
      onSend={handleSend}
      onUploadClick={handleUploadClick}
      loading={loading}
      inputRef={inputRef}
      handleKeyPress={handleKeyPress}
      selectedFile={selectedFile}
      onRemoveFile={onRemoveFile}
      {...voiceInputProps}
    />
  </FloatingChatComposer>
);

const DesktopChatArea = ({
  selectedChatId,
  messages,
  loading,
  messagesLoading,
  messagesLoadError = null,
  input,
  setInput,
  handleSend,
  handleUploadClick,
  inputRef,
  handleKeyPress,
  selectedFile,
  onRemoveFile,
  fileInputRef,
  documentInputRef,
  handleFileSelect,
  handleSuggestionClick,
  messagesScrollRef,
  parsedChatData,
  onUiAction,
  voiceInputProps = {},
}) => {
  const chatTitle = selectedChatId ? parsedChatData?.primaryTitle || "New Chat" : null;

  const dateLabel = new Date()
    .toLocaleDateString("en-US", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    })
    .toUpperCase();

  const composerProps = {
    input,
    setInput,
    handleSend,
    handleUploadClick,
    loading,
    inputRef,
    handleKeyPress,
    selectedFile,
    onRemoveFile,
    fileInputRef,
    documentInputRef,
    handleFileSelect,
    voiceInputProps,
  };

  if (messagesLoading && messages.length === 0) {
    return (
      <div className="flex-1 flex flex-col bg-white border-l border-[#E4E7EC] min-w-0">
        <div className="flex-1 overflow-y-auto min-h-0">
          <ChatConversationSkeleton rows={2} showAvatar={false} />
        </div>
      </div>
    );
  }

  if (messagesLoadError && messages.length === 0) {
    return (
      <div className="flex-1 flex flex-col bg-white border-l border-[#E4E7EC] min-w-0">
        <div className="flex-1 flex items-center justify-center px-8">
          <p className="text-[14px] text-[#D92D20] text-center">{messagesLoadError}</p>
        </div>
      </div>
    );
  }

  if (!selectedChatId) {
    return (
      <div className="flex-1 flex flex-col bg-white border-l border-[#E4E7EC] min-w-0">
        <ChatEmptyState
          title="No chat selected"
          description="Select a chat from the list or start a new conversation to continue."
          className="flex-1"
        />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-white border-l border-[#E4E7EC] min-w-0 min-h-0">
      {messages.length > 0 ? (
        <div className="flex-shrink-0 flex items-center px-6 py-4 border-b border-[#E4E7EC]">
          <h2 className="text-[18px] font-[600] text-[#11121A]">{chatTitle}</h2>
        </div>
      ) : null}

      <div
        ref={messagesScrollRef}
        className="flex-1 overflow-y-auto overflow-x-hidden px-4 md:px-8 pt-6 pb-4 min-h-0"
      >
        {messages.length === 0 ? (
          <div className="max-w-3xl mx-auto">
            <p className="text-[15px] text-[#1D2739] font-[400] mb-5">
              Hi, I'm Dockie. What can I help you with today?
            </p>
            <div className="flex flex-col gap-1">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => handleSuggestionClick({ title: s.label })}
                  className="flex items-center gap-3 text-left px-4 py-3 rounded-xl w-fit hover:bg-[#F9FAFB] active:bg-[#F0F2F5] transition-colors text-[14px] text-[#344054]"
                >
                  <span className="text-[16px]">{s.icon}</span>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-center mb-6">
              <span className="text-[11px] font-[600] tracking-widest text-[#98A2B3]">{dateLabel}</span>
            </div>
            {messages.map((message, index) => (
              <MessageBubble
                key={message.id ?? `msg-${index}`}
                message={message}
                isUser={message.role === "user"}
                formatTimestamp={formatTimestamp}
                onUiAction={onUiAction}
                disabled={voiceInputProps?.voiceActive || voiceInputProps?.loading}
              />
            ))}
            {loading && !messages.some((m) => m.isStreaming) ? <TypingIndicator /> : null}
          </>
        )}
      </div>

      {composer(composerProps)}
    </div>
  );
};

export default DesktopChatArea;
